import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Backdrop,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReplayIcon from '@mui/icons-material/Replay';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const getStatusChip = (status) => {
  if (!status) return { label: '-', color: 'default' };
  const s = status.toLowerCase();
  if (s.includes('added')) return { label: 'Added to NS', color: 'success' };
  if (s.includes('failed')) return { label: 'Failed', color: 'error' };
  return { label: 'Pending', color: 'warning' };
};

const detailTypeMap = {
  L: 'Labor',
  M: 'Material',
  T: 'Tax',
  O: 'Trip Charge',
};

const EcoTrakDashboard = () => {
  const { user: firebaseUser, loading: authenticating } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, totalRows: 0, totalPages: 0 });
  const [summary, setSummary] = useState({ success: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Expanded rows: { 'vendorId-invoiceNumber': { loading, data } }
  const [expandedRows, setExpandedRows] = useState({});

  // Retry modal state
  const [retryModal, setRetryModal] = useState({ open: false, row: null });
  const [retryMode, setRetryMode] = useState('existing'); // 'existing' or 'edit'
  const [retryDetails, setRetryDetails] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const [retryDetailLoading, setRetryDetailLoading] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [retryForm, setRetryForm] = useState({ storeNumber: '', vendorId: '', invoiceDate: '', invoiceNumber: '', lineItems: [] });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters from URL params
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [storeNumber, setStoreNumber] = useState(searchParams.get('storeNumber') || '');
  const [vendorName, setVendorName] = useState(searchParams.get('vendorName') || '');
  const [dateFrom, setDateFrom] = useState(() => {
    if (searchParams.get('dateFrom')) return new Date(searchParams.get('dateFrom'));
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [dateTo, setDateTo] = useState(
    searchParams.get('dateTo') ? new Date(searchParams.get('dateTo')) : new Date()
  );

  // Sort state
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'invoiceDate');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');

  // Pagination state (API is 1-based)
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize')) || 50);

  // Debounced search value (state, so it triggers fetchInvoices re-creation)
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');

  // Sync filters to URL
  const updateURL = useCallback(
    (overrides = {}) => {
      const p = {
        search: overrides.search !== undefined ? overrides.search : debouncedSearch,
        status: overrides.status !== undefined ? overrides.status : status,
        storeNumber: overrides.storeNumber !== undefined ? overrides.storeNumber : storeNumber,
        vendorName: overrides.vendorName !== undefined ? overrides.vendorName : vendorName,
        dateFrom: overrides.dateFrom !== undefined
          ? overrides.dateFrom
          : dateFrom
            ? dateFrom.toISOString().split('T')[0]
            : '',
        dateTo: overrides.dateTo !== undefined
          ? overrides.dateTo
          : dateTo
            ? dateTo.toISOString().split('T')[0]
            : '',
        sortBy: overrides.sortBy !== undefined ? overrides.sortBy : sortBy,
        sortOrder: overrides.sortOrder !== undefined ? overrides.sortOrder : sortOrder,
        page: String(overrides.page !== undefined ? overrides.page : page),
        pageSize: String(overrides.pageSize !== undefined ? overrides.pageSize : pageSize),
      };

      const newParams = new URLSearchParams();
      Object.entries(p).forEach(([key, val]) => {
        if (val && val !== '1' + '' && key === 'page' && val === '1') return;
        if (val && val !== '50' + '' && key === 'pageSize' && val === '50') return;
        if (val) newParams.set(key, val);
      });
      setSearchParams(newParams, { replace: true });
    },
    [debouncedSearch, status, storeNumber, vendorName, dateFrom, dateTo, sortBy, sortOrder, page, pageSize, setSearchParams]
  );

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    if (!firebaseUser) return;
    try {
      setLoading(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (status) params.set('status', status);
      if (storeNumber) params.set('storeNumber', storeNumber);
      if (vendorName) params.set('vendorName', vendorName);
      if (dateFrom) params.set('dateFrom', dateFrom.toISOString().split('T')[0]);
      if (dateTo) params.set('dateTo', dateTo.toISOString().split('T')[0]);

      const response = await axios.get(`/hd-api/ecotrak/invoices?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, pageSize: 50, totalRows: 0, totalPages: 0 });
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to fetch invoices. Please try again.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [firebaseUser, debouncedSearch, page, pageSize, sortBy, sortOrder, status, storeNumber, vendorName, dateFrom, dateTo]);

  // Fetch summary (respects same filters as the table)
  const fetchSummary = useCallback(async () => {
    if (!firebaseUser) return;
    try {
      const token = await firebaseUser.getIdToken(true);
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (storeNumber) params.set('storeNumber', storeNumber);
      if (vendorName) params.set('vendorName', vendorName);
      if (dateFrom) params.set('dateFrom', dateFrom.toISOString().split('T')[0]);
      if (dateTo) params.set('dateTo', dateTo.toISOString().split('T')[0]);
      const qs = params.toString();
      const response = await axios.get(`/hd-api/ecotrak/invoices/summary${qs ? `?${qs}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  }, [firebaseUser, debouncedSearch, storeNumber, vendorName, dateFrom, dateTo]);

  // Fetch summary when filters change
  useEffect(() => {
    if (firebaseUser) {
      fetchSummary();
    }
  }, [firebaseUser, fetchSummary]);

  // Fetch invoices when filters/pagination/sort change
  useEffect(() => {
    if (firebaseUser) {
      fetchInvoices();
    }
  }, [firebaseUser, fetchInvoices]);

  // Debounce search input — only update debounced state; the main effect handles fetching
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      updateURL({ search, page: 1 });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Fetch detail for expanded row
  const fetchDetail = async (vendorId, invoiceNumber) => {
    const key = `${vendorId}-${invoiceNumber}`;
    setExpandedRows((prev) => ({
      ...prev,
      [key]: { loading: true, data: null },
    }));
    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get(
        `/hd-api/ecotrak/invoices/${encodeURIComponent(vendorId)}/${encodeURIComponent(invoiceNumber)}/details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExpandedRows((prev) => ({
        ...prev,
        [key]: { loading: false, data: response.data.data || [] },
      }));
    } catch (err) {
      console.error('Error fetching detail:', err);
      setExpandedRows((prev) => ({
        ...prev,
        [key]: { loading: false, data: null, error: true },
      }));
    }
  };

  const handleRowClick = (row) => {
    const key = `${row.vendorId}-${row.invoiceNumber}`;
    if (expandedRows[key]) {
      setExpandedRows((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      fetchDetail(row.vendorId, row.invoiceNumber);
    }
  };

  const handleSort = (column) => {
    let newOrder = 'desc';
    if (sortBy === column) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortBy(column);
    setSortOrder(newOrder);
    setPage(1);
    updateURL({ sortBy: column, sortOrder: newOrder, page: 1 });
  };

  const handleStatusCardClick = (statusValue) => {
    const newStatus = status === statusValue ? '' : statusValue;
    setStatus(newStatus);
    setPage(1);
    updateURL({ status: newStatus, page: 1 });
  };

  const handleFilterChange = (setter, key) => (e) => {
    const val = e.target.value;
    setter(val);
    setPage(1);
    updateURL({ [key]: val, page: 1 });
  };

  const handleDateFromChange = (date) => {
    setDateFrom(date);
    setPage(1);
    updateURL({ dateFrom: date ? date.toISOString().split('T')[0] : '', page: 1 });
  };

  const handleDateToChange = (date) => {
    setDateTo(date);
    setPage(1);
    updateURL({ dateTo: date ? date.toISOString().split('T')[0] : '', page: 1 });
  };

  const handlePageChange = (e, newPage) => {
    const apiPage = newPage + 1; // MUI is 0-based, API is 1-based
    setPage(apiPage);
    updateURL({ page: apiPage });
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setPage(1);
    updateURL({ pageSize: newSize, page: 1 });
  };

  const handleExport = async () => {
    if (!firebaseUser) return;
    try {
      setExporting(true);
      const token = await firebaseUser.getIdToken(true);

      const params = new URLSearchParams();
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (status) params.set('status', status);
      if (storeNumber) params.set('storeNumber', storeNumber);
      if (vendorName) params.set('vendorName', vendorName);
      if (dateFrom) params.set('dateFrom', dateFrom.toISOString().split('T')[0]);
      if (dateTo) params.set('dateTo', dateTo.toISOString().split('T')[0]);

      const response = await axios.get(`/hd-api/ecotrak/invoices/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ecotrak-invoices.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setStatus('');
    setStoreNumber('');
    setVendorName('');
    setDateFrom(null);
    setDateTo(null);
    setSortBy('invoiceDate');
    setSortOrder('desc');
    setPage(1);
    setPageSize(50);
    setDebouncedSearch('');
    setSearchParams({}, { replace: true });
  };

  // Retry modal handlers
  const openRetryModal = async (e, row) => {
    e.stopPropagation();
    setRetryModal({ open: true, row });
    setRetryMode('existing');
    setRetryError(null);
    setRetryDetailLoading(true);
    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get(
        `/hd-api/ecotrak/invoices/${encodeURIComponent(row.vendorId)}/${encodeURIComponent(row.invoiceNumber)}/details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const details = response.data.data || [];
      setRetryDetails(details);
      const header = details.find((d) => d.recordType === 'H') || details[0] || {};
      const lines = details.filter((d) => d.recordType === 'D');
      setRetryForm({
        storeNumber: header.storeNumber || row.storeNumber || '',
        vendorId: header.vendorId || row.vendorId || '',
        invoiceDate: header.invoiceDate || row.invoiceDate || '',
        invoiceNumber: header.invoiceNumber || row.invoiceNumber || '',
        lineItems: lines.map((l) => ({
          invoiceDetail: l.invoiceDetail,
          accountCode: l.accountCode || '',
          amount: l.amount != null ? Number(l.amount) : 0,
        })),
      });
    } catch (err) {
      console.error('Error loading retry details:', err);
      setRetryError('Failed to load invoice details.');
    } finally {
      setRetryDetailLoading(false);
    }
  };

  const closeRetryModal = () => {
    setRetryModal({ open: false, row: null });
    setRetryDetails(null);
    setRetryError(null);
    setRetryForm({ storeNumber: '', vendorId: '', invoiceDate: '', invoiceNumber: '', lineItems: [] });
  };

  const handleRetrySubmit = async () => {
    const row = retryModal.row;
    if (!row || !firebaseUser) return;
    setRetryLoading(true);
    setRetryError(null);
    try {
      const token = await firebaseUser.getIdToken(true);
      const body = retryMode === 'edit' ? {
        storeNumber: retryForm.storeNumber,
        vendorId: retryForm.vendorId,
        invoiceDate: retryForm.invoiceDate,
        invoiceNumber: retryForm.invoiceNumber,
        lineItems: retryForm.lineItems,
      } : {};

      const response = await axios.post(
        `/hd-api/ecotrak/invoices/${encodeURIComponent(row.vendorId)}/${encodeURIComponent(row.invoiceNumber)}/${encodeURIComponent(row.workOrderNumber)}/retry`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      closeRetryModal();
      setSnackbar({
        open: true,
        message: `Invoice #${row.invoiceNumber} successfully created in NetSuite — Bill ID: ${response.data.netsuiteInternalId}`,
        severity: 'success',
      });
      fetchInvoices();
      fetchSummary();
    } catch (err) {
      console.error('Error retrying invoice:', err);
      if (err?.response?.data?.alreadyResolved) {
        closeRetryModal();
        setSnackbar({
          open: true,
          message: `Invoice #${row.invoiceNumber} was already resolved by the automated process (Bill ID: ${err.response.data.netsuiteInternalId || 'unknown'}). Refreshing...`,
          severity: 'info',
        });
        fetchInvoices();
        fetchSummary();
      } else {
        const msg = err?.response?.data?.error || 'Failed to retry invoice. Please try again.';
        setRetryError(msg);
      }
    } finally {
      setRetryLoading(false);
    }
  };

  const updateLineItem = (idx, field, value) => {
    setRetryForm((prev) => {
      const lineItems = [...prev.lineItems];
      lineItems[idx] = { ...lineItems[idx], [field]: field === 'amount' ? Number(value) || 0 : value };
      return { ...prev, lineItems };
    });
  };

  const retryFormTotal = retryForm.lineItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  const columns = [
    { id: 'storeNumber', label: 'Store #', width: 70 },
    { id: 'vendorId', label: 'Vendor ID', width: 90 },
    { id: 'vendorName', label: 'Vendor', width: 180 },
    { id: 'invoiceNumber', label: 'Invoice #', width: 100 },
    { id: 'invoiceDate', label: 'Invoice Date', width: 110 },
    { id: 'workOrderNumber', label: 'Work Order #', width: 110 },
    { id: 'totalAmount', label: 'Amount', width: 100, align: 'right' },
    { id: 'status', label: 'Status', width: 120 },
    { id: 'lastUpdated', label: 'Last Updated', width: 130 },
    { id: 'netsuiteInternalId', label: 'NS Bill ID', width: 100, sortable: false },
    { id: 'nsFileId', label: 'PDF', width: 50, sortable: false, align: 'center' },
    { id: 'action', label: 'Action', width: 60, sortable: false, align: 'center' },
  ];

  if (authenticating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '20%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth='xl'>
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          {/* Title Paper */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReceiptLongIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='h5'
                  sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}
                >
                  EcoTrak Invoices
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  View EcoTrak invoice data and NetSuite vendor bill status
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            {[
              { key: 'success', label: 'Success', count: summary.success, color: '#22c55e', bgColor: '#f0fdf4' },
              { key: 'pending', label: 'Pending', count: summary.pending, color: '#f59e0b', bgColor: '#fffbeb' },
              { key: 'failed', label: 'Failed', count: summary.failed, color: '#ef4444', bgColor: '#fef2f2' },
            ].map((card) => (
              <Card
                key={card.key}
                elevation={0}
                sx={{
                  minWidth: 140,
                  flex: 1,
                  maxWidth: 200,
                  border: '1px solid',
                  borderColor: status === card.key ? card.color : 'divider',
                  backgroundColor: status === card.key ? card.bgColor : 'white',
                  transition: 'all 0.2s',
                }}
              >
                <CardActionArea onClick={() => handleStatusCardClick(card.key)}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: card.color,
                        }}
                      />
                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        {card.label}
                      </Typography>
                    </Box>
                    <Typography variant='h5' sx={{ fontWeight: 700, mt: 0.5, color: card.color }}>
                      {card.count.toLocaleString()}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          {/* Filter Bar */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              {/* Search */}
              <TextField
                size='small'
                placeholder='Search invoices...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position='end'>
                      <IconButton size='small' onClick={() => setSearch('')}>
                        <ClearIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Store # */}
              <TextField
                size='small'
                label='Store #'
                value={storeNumber}
                onChange={handleFilterChange(setStoreNumber, 'storeNumber')}
                sx={{ width: 100 }}
                autoComplete='off'
              />

              {/* Vendor */}
              <TextField
                size='small'
                label='Vendor'
                value={vendorName}
                onChange={handleFilterChange(setVendorName, 'vendorName')}
                sx={{ width: 150 }}
                autoComplete='off'
              />

              {/* Status */}
              <FormControl size='small' sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={handleFilterChange(setStatus, 'status')}
                  label='Status'
                >
                  <MenuItem value=''>All</MenuItem>
                  <MenuItem value='success'>Success</MenuItem>
                  <MenuItem value='pending'>Pending</MenuItem>
                  <MenuItem value='failed'>Failed</MenuItem>
                </Select>
              </FormControl>

              {/* Date Range */}
              <DatePicker
                selected={dateFrom}
                onChange={handleDateFromChange}
                selectsStart
                startDate={dateFrom}
                endDate={dateTo}
                placeholderText='From Date'
                isClearable
                customInput={
                  <TextField size='small' label='From Date' sx={{ width: 140 }} autoComplete='off' />
                }
              />
              <DatePicker
                selected={dateTo}
                onChange={handleDateToChange}
                selectsEnd
                startDate={dateFrom}
                endDate={dateTo}
                minDate={dateFrom}
                placeholderText='To Date'
                isClearable
                customInput={
                  <TextField size='small' label='To Date' sx={{ width: 140 }} autoComplete='off' />
                }
              />

              {/* Export CSV */}
              <Tooltip title='Export current filtered results as CSV'>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                  onClick={handleExport}
                  disabled={exporting}
                >
                  CSV
                </Button>
              </Tooltip>

              {/* Clear Filters */}
              {(search || status || storeNumber || vendorName || dateFrom || dateTo) && (
                <Button size='small' onClick={handleClearFilters} startIcon={<ClearIcon />}>
                  Clear
                </Button>
              )}
            </Box>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Data Table */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <TableContainer sx={{ maxHeight: 'calc(100vh - 440px)' }}>
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40, backgroundColor: '#fafafa' }} />
                    {columns.map((col) => (
                      <TableCell
                        key={col.id}
                        sx={{
                          fontWeight: 600,
                          backgroundColor: '#fafafa',
                          width: col.width,
                          ...(col.align && { textAlign: col.align }),
                        }}
                      >
                        {col.sortable !== false ? (
                          <TableSortLabel
                            active={sortBy === col.id}
                            direction={sortBy === col.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color='text.secondary'>
                          No invoices found. Try adjusting your filters.
                        </Typography>
                        {(search || status || storeNumber || vendorName || dateFrom || dateTo) && (
                          <Button size='small' onClick={handleClearFilters} sx={{ mt: 1 }}>
                            Clear Filters
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => {
                      const key = `${row.vendorId}-${row.invoiceNumber}`;
                      const expanded = expandedRows[key];
                      const isOpen = !!expanded;
                      const statusChip = getStatusChip(row.status);

                      return (
                        <React.Fragment key={key}>
                          <TableRow
                            hover
                            onClick={() => handleRowClick(row)}
                            sx={{ cursor: 'pointer', '& > *': { borderBottom: isOpen ? 'unset' : undefined } }}
                          >
                            <TableCell sx={{ width: 40 }}>
                              <IconButton size='small'>
                                {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>{row.storeNumber}</TableCell>
                            <TableCell>{row.vendorId}</TableCell>
                            <TableCell>
                              <Typography variant='body2' noWrap sx={{ maxWidth: 180 }}>
                                {row.vendorName || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant='body2' sx={{ fontWeight: 500, color: 'primary.main' }}>
                                {row.invoiceNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.invoiceDate}</TableCell>
                            <TableCell>{row.workOrderNumber || '-'}</TableCell>
                            <TableCell align='right'>
                              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                                {formatCurrency(row.totalAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={statusChip.label} size='small' color={statusChip.color} />
                            </TableCell>
                            <TableCell>
                              {row.lastUpdated
                                ? new Date(row.lastUpdated).toLocaleDateString('en-US')
                                : '-'}
                            </TableCell>
                            <TableCell>{row.netsuiteInternalId || '-'}</TableCell>
                            <TableCell align='center'>
                              {row.nsFileId ? (
                                <CheckIcon fontSize='small' color='success' />
                              ) : (
                                <Typography variant='body2' color='text.secondary'>-</Typography>
                              )}
                            </TableCell>
                            <TableCell align='center'>
                              {row.statusCode === 2 && (
                                <Tooltip title='Retry failed invoice'>
                                  <IconButton size='small' color='error' onClick={(e) => openRetryModal(e, row)}>
                                    <ReplayIcon fontSize='small' />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Expanded Detail Row */}
                          <TableRow>
                            <TableCell sx={{ py: 0 }} colSpan={columns.length + 1}>
                              <Collapse in={isOpen} timeout='auto' unmountOnExit>
                                <Box sx={{ py: 2, px: 2 }}>
                                  {expanded?.loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                      <CircularProgress size={24} />
                                    </Box>
                                  ) : expanded?.error ? (
                                    <Alert severity='error' sx={{ mb: 1 }}>
                                      Failed to load line items.
                                    </Alert>
                                  ) : expanded?.data ? (
                                    <Table size='small'>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Detail</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Qty</TableCell>
                                          <TableCell align='right' sx={{ fontWeight: 600 }}>Amount</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Asset</TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>PO #</TableCell>
                                          <TableCell align='right' sx={{ fontWeight: 600 }}>NTE</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {expanded.data.map((detail, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>
                                              <Chip
                                                label={detail.recordType === 'H' ? 'Header' : 'Detail'}
                                                size='small'
                                                variant='outlined'
                                                color={detail.recordType === 'H' ? 'info' : 'default'}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              {detailTypeMap[detail.invoiceDetail] || detail.invoiceDetail || '-'}
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant='body2' noWrap sx={{ maxWidth: 250 }}>
                                                {detail.description || '-'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>{detail.accountCode || '-'}</TableCell>
                                            <TableCell>{detail.quantity || '-'}</TableCell>
                                            <TableCell align='right'>{formatCurrency(detail.amount)}</TableCell>
                                            <TableCell>
                                              <Typography variant='body2' noWrap sx={{ maxWidth: 150 }}>
                                                {detail.assetName || '-'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>{detail.purchaseOrder || '-'}</TableCell>
                                            <TableCell align='right'>
                                              {detail.nte != null ? formatCurrency(detail.nte) : '-'}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  ) : null}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              count={pagination.totalRows}
              page={page - 1}
              onPageChange={handlePageChange}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </Paper>
        </Box>
      </Container>

      {/* Retry Modal */}
      <Dialog open={retryModal.open} onClose={closeRetryModal} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Retry Invoice #{retryModal.row?.invoiceNumber}
        </DialogTitle>
        <DialogContent dividers>
          {retryModal.row && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                Vendor: {retryModal.row.vendorName} ({retryModal.row.vendorId})
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Store #: {retryModal.row.storeNumber} &nbsp;&nbsp; Work Order: {retryModal.row.workOrderNumber || '-'}
              </Typography>
            </Box>
          )}

          {retryError && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setRetryError(null)}>
              {retryError}
            </Alert>
          )}

          {retryDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : retryDetails ? (
            <>
              <RadioGroup
                value={retryMode}
                onChange={(e) => setRetryMode(e.target.value)}
                sx={{ mb: 2 }}
              >
                <FormControlLabel value='existing' control={<Radio size='small' />} label='Retry with existing data' />
                <FormControlLabel value='edit' control={<Radio size='small' />} label='Edit before retrying' />
              </RadioGroup>

              <Divider sx={{ mb: 2 }} />

              {/* Header Fields */}
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>Header Fields</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField
                  size='small'
                  label='Store #'
                  value={retryForm.storeNumber}
                  onChange={(e) => setRetryForm((prev) => ({ ...prev, storeNumber: e.target.value }))}
                  disabled={retryMode === 'existing'}
                  sx={{ width: 120 }}
                />
                <TextField
                  size='small'
                  label='Vendor ID'
                  value={retryForm.vendorId}
                  onChange={(e) => setRetryForm((prev) => ({ ...prev, vendorId: e.target.value }))}
                  disabled={retryMode === 'existing'}
                  sx={{ width: 120 }}
                />
                <TextField
                  size='small'
                  label='Invoice Date'
                  value={retryForm.invoiceDate}
                  onChange={(e) => setRetryForm((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                  disabled={retryMode === 'existing'}
                  sx={{ width: 140 }}
                />
                <TextField
                  size='small'
                  label='Invoice #'
                  value={retryForm.invoiceNumber}
                  disabled
                  sx={{ width: 120 }}
                />
              </Box>

              {/* Line Items */}
              {retryForm.lineItems.length > 0 && (
                <>
                  <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>Line Items</Typography>
                  <Table size='small' sx={{ mb: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Account Code</TableCell>
                        <TableCell align='right' sx={{ fontWeight: 600 }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {retryForm.lineItems.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{detailTypeMap[item.invoiceDetail] || item.invoiceDetail || '-'}</TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              value={item.accountCode}
                              onChange={(e) => updateLineItem(idx, 'accountCode', e.target.value)}
                              disabled={retryMode === 'existing'}
                              sx={{ width: 120 }}
                              variant='outlined'
                            />
                          </TableCell>
                          <TableCell align='right'>
                            <TextField
                              size='small'
                              type='number'
                              value={item.amount}
                              onChange={(e) => updateLineItem(idx, 'amount', e.target.value)}
                              disabled={retryMode === 'existing'}
                              sx={{ width: 120 }}
                              variant='outlined'
                              inputProps={{ step: '0.01' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Typography variant='body2' sx={{ fontWeight: 600, textAlign: 'right', mr: 1 }}>
                    Total: {formatCurrency(retryFormTotal)}
                  </Typography>
                </>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeRetryModal} disabled={retryLoading}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleRetrySubmit}
            disabled={retryLoading || retryDetailLoading || !retryDetails}
            startIcon={retryLoading ? <CircularProgress size={16} /> : <ReplayIcon />}
          >
            Retry Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
    </div>
  );
};

export default EcoTrakDashboard;
